import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { listOrganizations, type OrgEntry } from '../../api/organizations'
import { useAuth } from '../../hooks/useAuth'
import { resolveOrgSlug, SUPERADMIN_DEFAULT_ORG_SLUG } from '../../utils/org-slug'

interface OrgContextValue {
  /** Selected org id for superadmin. Null = no org selected. */
  selectedOrgId: number | null
  setSelectedOrgId: (id: number) => void
  /** All active orgs (superadmin only). */
  orgs: OrgEntry[]
  isLoading: boolean
  /** True when the current user is superadmin. */
  isSuperAdmin: boolean
  /**
   * True when a superadmin is browsing the HQ org (pradeepcgtech). Only there
   * is the cross-org switcher available — a superadmin switched into any other
   * org (e.g. /vindago/…) stays scoped to the URL org with no dropdown.
   */
  isHqSuperadmin: boolean
}

const OrgContext = createContext<OrgContextValue | null>(null)

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext)
  if (!ctx) {
    throw new Error('useOrgContext must be used within OrgProvider')
  }
  return ctx
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'superadmin'
  // Cross-org switcher is HQ-only (mirrors OrgPromptsPage's isHqSuperadmin): a
  // superadmin switched into any other org stays scoped to the URL org.
  const isHqSuperadmin = isSuperAdmin && resolveOrgSlug() === SUPERADMIN_DEFAULT_ORG_SLUG
  const [orgs, setOrgs] = useState<OrgEntry[]>([])
  const [selectedOrgId, setSelectedOrgIdState] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isSuperAdmin) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    listOrganizations()
      .then((data) => {
        if (cancelled) return
        const active = data.filter((o) => o.isActive)
        setOrgs(active)

        // The admin users screen must only show users of the URL org
        // (e.g. /vindago/admin/users shows vindago users). Prefer the URL
        // path slug, then ?org=<slug> for deep-links, then first active.
        const params = new URLSearchParams(window.location.search)
        const querySlug = params.get('org')
        const pathSlug = resolveOrgSlug()
        const pathMatch = pathSlug ? active.find((o) => o.slug === pathSlug) : null
        const queryMatch = querySlug ? active.find((o) => o.slug === querySlug) : null
        const match = pathMatch ?? queryMatch
        if (match) {
          setSelectedOrgIdState(match.id)
        } else if (active.length > 0) {
          setSelectedOrgIdState(active[0].id)
        }
      })
      .catch(() => {
        // Failed to load orgs — superadmin sees empty state.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isSuperAdmin])

  const setSelectedOrgId = useCallback(
    (id: number) => {
      setSelectedOrgIdState(id)
      // Persist selection in URL for deep-link/refresh.
      const org = orgs.find((o) => o.id === id)
      if (org) {
        const url = new URL(window.location.href)
        url.searchParams.set('org', org.slug)
        window.history.replaceState(null, '', url.toString())
      }
    },
    [orgs]
  )

  return (
    <OrgContext.Provider
      value={{
        selectedOrgId,
        setSelectedOrgId,
        orgs,
        isLoading,
        isSuperAdmin,
        isHqSuperadmin,
      }}
    >
      {children}
    </OrgContext.Provider>
  )
}

/**
 * Org switcher dropdown — rendered only for a superadmin browsing the HQ org.
 * Everywhere else (org admins, or a superadmin switched into another org) the
 * page is scoped to the URL org and no switcher is shown.
 * Uses OrgContext for state; no props needed.
 */
export function OrgPicker() {
  const { selectedOrgId, setSelectedOrgId, orgs, isLoading, isHqSuperadmin } = useOrgContext()

  if (!isHqSuperadmin) return null
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-harbor-text-secondary text-sm">
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-emerald-500" />
        Loading orgs…
      </div>
    )
  }
  if (orgs.length === 0) {
    return <p className="text-sm text-harbor-text-muted">No active organizations.</p>
  }

  return (
    <div className="flex items-center gap-3">
      <label htmlFor="org-picker" className="text-sm font-medium text-harbor-text-secondary">
        Organization
      </label>
      <select
        id="org-picker"
        value={selectedOrgId ?? ''}
        onChange={(e) => {
          const id = Number(e.target.value)
          if (Number.isSafeInteger(id)) setSelectedOrgId(id)
        }}
        className="px-3 py-2 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      >
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name} ({org.slug})
          </option>
        ))}
      </select>
    </div>
  )
}
